import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Text, Box } from '@chakra-ui/react';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal } from '@morressier/alembic';
import { useBlocker, useLocation } from 'react-router-dom';
import {
  SUBMISSION_UPLOAD_LEAVE_EVENT,
  SubmissionUploadLeaveDetail
} from 'src/utils/submissionUploadLeave';

type SaveCurrentProgressFn = () => Promise<boolean | void> | boolean | void;

type LeaveGuardState = {
  hasUnsavedChanges: boolean;
  saveCurrentProgress: SaveCurrentProgressFn | null;
};

type LeaveGuardContextValue = {
  registerLeaveGuardState: (state: LeaveGuardState) => void;
  allowNextNavigationWithoutPrompt: () => void;
};

const LeaveGuardContext = createContext<LeaveGuardContextValue | null>(null);
let skipNextNavigationPrompt = false;

export const allowSubmissionNavigationWithoutPrompt = () => {
  skipNextNavigationPrompt = true;
};

export const shouldBypassSubmissionNavigationPrompt = () =>
  skipNextNavigationPrompt;

export const useSubmissionLeaveGuardRegistration = (
  state: LeaveGuardState
) => {
  const context = useContext(LeaveGuardContext);

  if (!context) {
    throw new Error(
      'useSubmissionLeaveGuardRegistration must be used within SubmissionLeaveGuardProvider'
    );
  }

  const { registerLeaveGuardState } = context;
  const { hasUnsavedChanges, saveCurrentProgress } = state;

  useEffect(() => {
    registerLeaveGuardState({
      hasUnsavedChanges,
      saveCurrentProgress
    });

    return () => {
      registerLeaveGuardState({
        hasUnsavedChanges: false,
        saveCurrentProgress: null
      });
    };
  }, [registerLeaveGuardState, hasUnsavedChanges, saveCurrentProgress]);
};

export const useSubmissionLeaveGuardActions = () => {
  const context = useContext(LeaveGuardContext);

  if (!context) {
    throw new Error(
      'useSubmissionLeaveGuardActions must be used within SubmissionLeaveGuardProvider'
    );
  }

  return {
    allowNextNavigationWithoutPrompt: context.allowNextNavigationWithoutPrompt
  };
};

const SubmissionLeaveWarningModal = ({
  isOpen,
  isSaving,
  onClose,
  onSaveAndLeave,
  onExitWithoutSaving,
  hasSaveAction
}: {
  isOpen: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSaveAndLeave: () => void;
  onExitWithoutSaving: () => void;
  hasSaveAction: boolean;
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    width={{ base: 'calc(100vw - 16px)', md: '760px' } as any}
    closeButtonProps={{ hidden: true }}
  >
    <Flex direction="column" gap={4} p={{ base: 4, md: 6 }}>
      <Flex alignItems="center" gap={3}>
        <Box color="actionSecondaryCautionContent" fontSize="28px">
          <FontAwesomeIcon icon={faTriangleExclamation} />
        </Box>
        <Text textStyle="headingGroup">Save your progress?</Text>
      </Flex>
      <Text textStyle="textBody">
        You're leaving your submission process mid-way. Save it as a draft so
        you can pick up right where you left off, or stay and finish now.
      </Text>
      <Flex justifyContent="flex-end" gap={2} flexWrap="wrap">
        <Button variant="secondary" type="button" onClick={onClose} isDisabled={isSaving}>
          Continue Submission
        </Button>
        <Button
          variant="secondary"
          type="button"
          onClick={onExitWithoutSaving}
          isDisabled={isSaving}
        >
          Exit without save Draft
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={onSaveAndLeave}
          isLoading={isSaving}
          isDisabled={!hasSaveAction}
        >
          Save as Draft & Leave
        </Button>
      </Flex>
    </Flex>
  </Modal>
);

export const SubmissionLeaveGuardProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveCurrentProgress, setSaveCurrentProgress] =
    useState<SaveCurrentProgressFn | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const allowNextNavigationWithoutPromptRef = useRef(false);
  const location = useLocation();

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (
      allowNextNavigationWithoutPromptRef.current ||
      skipNextNavigationPrompt
    ) {
      return false;
    }

    return (
      hasUnsavedChanges &&
      currentLocation.pathname !== nextLocation.pathname
    );
  });

  const registerLeaveGuardState = useCallback((state: LeaveGuardState) => {
    setHasUnsavedChanges(state.hasUnsavedChanges);
    setSaveCurrentProgress(() => state.saveCurrentProgress);
  }, []);

  const allowNextNavigationWithoutPrompt = useCallback(() => {
    allowNextNavigationWithoutPromptRef.current = true;
  }, []);

  const openLeaveWarning = useCallback((action?: () => void) => {
    pendingActionRef.current = action ?? null;
    setIsModalOpen(true);
  }, []);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      openLeaveWarning(() => blocker.proceed());
    }
  }, [blocker, blocker.state, openLeaveWarning]);

  useEffect(() => {
    const handleLeaveRequest = (event: Event) => {
      const customEvent = event as CustomEvent<SubmissionUploadLeaveDetail>;
      const nextAction = customEvent.detail?.action;

      if (
        allowNextNavigationWithoutPromptRef.current ||
        skipNextNavigationPrompt
      ) {
        return;
      }

      if (!hasUnsavedChanges) {
        return;
      }

      customEvent.preventDefault();
      openLeaveWarning(nextAction);
    };

    window.addEventListener(
      SUBMISSION_UPLOAD_LEAVE_EVENT,
      handleLeaveRequest as EventListener
    );

    return () => {
      window.removeEventListener(
        SUBMISSION_UPLOAD_LEAVE_EVENT,
        handleLeaveRequest as EventListener
      );
    };
  }, [hasUnsavedChanges, openLeaveWarning]);

  useEffect(() => {
    if (skipNextNavigationPrompt) {
      skipNextNavigationPrompt = false;
    }
  }, [location.pathname]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    pendingActionRef.current = null;

    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  const saveAndLeave = useCallback(async () => {
    if (isSaving) return;

    const nextAction = pendingActionRef.current;

    if (!saveCurrentProgress) {
      nextAction?.();
      pendingActionRef.current = null;
      setIsModalOpen(false);
      return;
    }

    setIsSaving(true);

    try {
      const result = await saveCurrentProgress();
      if (result === false) {
        return;
      }

      setHasUnsavedChanges(false);
      setIsModalOpen(false);
      pendingActionRef.current = null;
      nextAction?.();
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, saveCurrentProgress]);

  const exitWithoutSaving = useCallback(() => {
    const nextAction = pendingActionRef.current;
    pendingActionRef.current = null;
    setIsModalOpen(false);
    nextAction?.();
  }, []);

  const contextValue = useMemo<LeaveGuardContextValue>(
    () => ({
      registerLeaveGuardState,
      allowNextNavigationWithoutPrompt
    }),
    [allowNextNavigationWithoutPrompt, registerLeaveGuardState]
  );

  return (
    <LeaveGuardContext.Provider value={contextValue}>
      {children}
      <SubmissionLeaveWarningModal
        isOpen={isModalOpen}
        isSaving={isSaving}
        onClose={closeModal}
        onSaveAndLeave={saveAndLeave}
        onExitWithoutSaving={exitWithoutSaving}
        hasSaveAction={Boolean(saveCurrentProgress)}
      />
    </LeaveGuardContext.Provider>
  );
};
